import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import HeroCarousel from './Hero';
import Footer from './Footer';
import { getImageUrl } from '../utils/imageHelper';
import { useCity } from '../contexts/CityContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CATEGORY_CHIPS = [
  { label: 'Strategy 🧩', value: 'Strategy' },
  { label: 'Party Games 🎉', value: 'Party Games' },
  { label: 'RPG 🎲', value: 'RPG' },
  { label: 'Card Games 🃏', value: 'Card Games' },
  { label: 'Co-op 🤝', value: 'Cooperative' },
  { label: 'Tournaments 🏆', value: 'Tournaments' },
  { label: 'Casual 😊', value: 'Casual' },
  { label: 'Beginner-Friendly ✨', value: 'Beginner' },
];

const DATE_CHIPS = [
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'This Weekend', value: 'weekend' },
  { label: 'This Week', value: 'week' },
  { label: 'All Upcoming', value: 'all' },
];

function isInDateRange(dateStr, range) {
  const ev = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const dayOfWeek = today.getDay();
  const saturday = new Date(today); saturday.setDate(today.getDate() + (6 - dayOfWeek));
  const sunday = new Date(saturday); sunday.setDate(saturday.getDate() + 1);
  const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + (7 - dayOfWeek));

  const evDay = new Date(ev.getFullYear(), ev.getMonth(), ev.getDate());
  if (range === 'today') return evDay.getTime() === today.getTime();
  if (range === 'tomorrow') return evDay.getTime() === tomorrow.getTime();
  if (range === 'weekend') return evDay >= saturday && evDay <= sunday;
  if (range === 'week') return ev >= today && ev <= endOfWeek;
  return ev >= today;
}

function EventCard({ event, onClick }) {
  const date = new Date(event.startDate);
  const mon = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(0,0,0,0.12)' }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer flex flex-col"
    >
      <div className="h-44 bg-primary relative overflow-hidden flex-shrink-0">
        {event.imageUrl ? (
          <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-14 h-14 text-white opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {/* Date badge */}
        <div className="absolute top-3 left-3 bg-white rounded-xl px-2.5 py-1.5 text-center min-w-[46px] shadow-lg">
          <div className="text-xs font-bold text-primary uppercase leading-none">{mon}</div>
          <div className="text-xl font-extrabold text-gray-900 leading-none">{day}</div>
        </div>
        {/* RSVP count */}
        <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs font-semibold px-2 py-1 rounded-full">
          {event._count?.rsvps ?? 0} going
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <p className="text-xs text-primary font-semibold mb-1 truncate">{event.club?.name}</p>
        <h3 className="text-sm font-bold text-gray-900 line-clamp-2 mb-3 leading-snug flex-1">{event.title}</h3>
        <div className="text-xs text-gray-500 space-y-1">
          <div className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {event.startTime}{event.endTime ? ` – ${event.endTime}` : ''}
          </div>
          {event.location && (
            <div className="flex items-center gap-1.5 truncate">
              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              {event.location}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function ClubCard({ club, onClick }) {
  const gameList = (club.games || '').split(',').filter(Boolean).slice(0, 3);

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(0,0,0,0.12)' }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer flex flex-col"
    >
      <div className="h-40 overflow-hidden relative">
        <img
          src={getImageUrl(club.image)}
          alt={club.name}
          className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://placehold.co/400x200/6366f1/white?text=Club'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
          <span className="text-white text-xs font-semibold bg-black/40 backdrop-blur-sm px-2 py-1 rounded-full">
            {club.memberCount || club.members || 0} members
          </span>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">{club.name}</h3>
        <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
          <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          {club.location || 'Location TBD'}
        </p>
        {gameList.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-auto">
            {gameList.map(g => (
              <span key={g} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                {g.trim()}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function Homepage() {
  const navigate = useNavigate();
  const { city } = useCity();

  const [events, setEvents] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [evLoading, setEvLoading] = useState(true);
  const [clubLoading, setClubLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [activeDateRange, setActiveDateRange] = useState('all');

  useEffect(() => {
    setEvLoading(true);
    fetch(`${API_URL}/events?upcoming=true&limit=8`)
      .then(r => r.json())
      .then(d => { if (d.success) setEvents(d.data); })
      .catch(() => {})
      .finally(() => setEvLoading(false));
  }, []);

  useEffect(() => {
    setClubLoading(true);
    fetch(`${API_URL}/clubs/featured`)
      .then(r => r.json())
      .then(d => { if (d.success) setClubs(d.data); })
      .catch(() => {})
      .finally(() => setClubLoading(false));
  }, []);

  const filteredEvents = events.filter(ev => {
    const dateOK = isInDateRange(ev.startDate, activeDateRange);
    const catOK = !activeCategory || (ev.title + (ev.description || '') + (ev.club?.name || ''))
      .toLowerCase().includes(activeCategory.toLowerCase());
    const cityOK = !city ||
      (ev.city || '').toLowerCase().includes(city.toLowerCase()) ||
      (ev.location || '').toLowerCase().includes(city.toLowerCase()) ||
      (ev.club?.name || '').toLowerCase().includes(city.toLowerCase());
    return dateOK && catOK && cityOK;
  });

  const filteredClubs = clubs.filter(club => {
    const catOK = !activeCategory || (club.games || '').toLowerCase().includes(activeCategory.toLowerCase());
    const cityOK = !city ||
      (club.city || '').toLowerCase().includes(city.toLowerCase()) ||
      (club.location || '').toLowerCase().includes(city.toLowerCase());
    return catOK && cityOK;
  });

  const totalClubs = clubs.length;
  const totalEvents = events.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Carousel */}
      <HeroCarousel />

      {/* Category Chips */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-30 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5">
            <button
              onClick={() => setActiveCategory('')}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                !activeCategory ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
              }`}
            >
              All
            </button>
            {CATEGORY_CHIPS.map(chip => (
              <button
                key={chip.value}
                onClick={() => setActiveCategory(activeCategory === chip.value ? '' : chip.value)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  activeCategory === chip.value ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Events Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Section header */}
          <div className="flex justify-between items-end mb-6">
            <div>
              <span className="text-primary font-semibold text-xs tracking-widest uppercase">Upcoming</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-0.5">
                Events Near You {city && <span className="text-primary">in {city}</span>}
              </h2>
              <p className="text-gray-500 text-sm mt-1">Game nights, tournaments & special gatherings from local clubs</p>
            </div>
            <button
              onClick={() => navigate('/events')}
              className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1 transition-colors flex-shrink-0"
            >
              View all
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Date Filter Chips */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-6">
            {DATE_CHIPS.map(chip => (
              <button
                key={chip.value}
                onClick={() => setActiveDateRange(chip.value)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  activeDateRange === chip.value ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:border-gray-900 hover:text-gray-900'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {evLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl shadow animate-pulse h-64" />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
              <svg className="mx-auto h-14 w-14 text-gray-200 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500 font-medium">No events match your filters</p>
              <p className="text-gray-400 text-sm mt-1">Try a different date range or clear the category filter</p>
              <button
                onClick={() => { setActiveDateRange('all'); setActiveCategory(''); }}
                className="mt-4 px-5 py-2 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
            >
              {filteredEvents.slice(0, 8).map(event => (
                <motion.div
                  key={event.id}
                  variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } }}
                >
                  <EventCard event={event} onClick={() => navigate(`/events?id=${event.id}`)} />
                </motion.div>
              ))}
            </motion.div>
          )}

          {filteredEvents.length > 0 && (
            <div className="text-center mt-8">
              <button
                onClick={() => navigate('/events')}
                className="px-8 py-3 bg-white border border-gray-200 text-gray-700 rounded-full font-semibold hover:border-primary hover:text-primary transition-colors shadow-sm"
              >
                Browse All Upcoming Events
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Clubs Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-between items-end mb-8">
            <div>
              <span className="text-primary font-semibold text-xs tracking-widest uppercase">Discover</span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-0.5">
                Popular Clubs {city && <span className="text-primary">in {city}</span>}
              </h2>
              <p className="text-gray-500 text-sm mt-1">Join vibrant communities of board game enthusiasts</p>
            </div>
            <button
              onClick={() => navigate('/find-clubs')}
              className="text-primary hover:text-primary/80 font-medium text-sm flex items-center gap-1 transition-colors flex-shrink-0"
            >
              View all clubs
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {clubLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl animate-pulse h-56" />
              ))}
            </div>
          ) : filteredClubs.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-2xl">
              <p className="text-gray-500 font-medium">No clubs found{city ? ` in ${city}` : ''}</p>
              <p className="text-gray-400 text-sm mt-1">Be the first to start a board game club in your area!</p>
              <button
                onClick={() => navigate('/partner-with-us')}
                className="mt-4 px-5 py-2 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90"
              >
                Start a Club
              </button>
            </div>
          ) : (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
            >
              {filteredClubs.slice(0, 6).map(club => (
                <motion.div
                  key={club.id}
                  variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } }}
                >
                  <ClubCard
                    club={club}
                    onClick={() => navigate(`/clubs/${club.slug || club.id}`)}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Community CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-primary to-blue-700 text-white">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4">Join the BoardVoyage Community</h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Connect with {totalClubs > 0 ? `${totalClubs}+ verified clubs` : 'board game clubs'} and
            discover {totalEvents > 0 ? `${totalEvents}+ upcoming events` : 'exciting events'} near you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-3 bg-white text-primary font-bold rounded-full hover:bg-gray-100 transition-colors shadow-lg"
            >
              Sign Up Free
            </button>
            <button
              onClick={() => navigate('/partner-with-us')}
              className="px-8 py-3 border-2 border-white text-white font-bold rounded-full hover:bg-white hover:text-primary transition-colors"
            >
              List Your Club
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Homepage;
